import { httpsCallable } from 'firebase/functions';
import { ensureAnonymousAuth, functions } from './firebase';
import type { SongSearchResult } from './itunesApi';

interface AppleSongAttributes {
  name?: string;
  artistName?: string;
  albumName?: string;
  artwork?: { url?: string };
  durationInMillis?: number;
  contentRating?: string;
  previews?: Array<{ url?: string }>;
  isrc?: string;
}

export interface AppleSongResource {
  id: string;
  attributes?: AppleSongAttributes;
}

interface AppleSearchResponse {
  results?: { songs?: { data?: AppleSongResource[] } };
}

interface AppleSongsResponse {
  data?: AppleSongResource[];
}

interface AppleMusicToken {
  token: string;
  expiresAt: number;
}

const APPLE_MUSIC_CATALOG_URL = 'https://api.music.apple.com/v1/catalog/us';
const TOKEN_STORAGE_KEY = 'vb_apple_music_token';
const TOKEN_REUSE_MARGIN_SECONDS = 3600;
const RETRY_AFTER_ERROR_MS = 60_000;
const MAX_APPLE_LIMIT = 25;
const ARTIST_FILTER_MINIMUM = 5;
const ISRC_PATTERN = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/i;

const getAppleMusicTokenCallable = httpsCallable<void, { token: string; expiresAt: number; storefront: string }>(
  functions,
  'getAppleMusicToken',
);

let cachedToken: AppleMusicToken | null = null;
let tokenRequest: Promise<string> | null = null;
let appleMusicUnavailable = false;
let retryBlockedUntil = 0;

export function resetAppleMusicClient(): void {
  cachedToken = null;
  tokenRequest = null;
  appleMusicUnavailable = false;
  retryBlockedUntil = 0;
}

export function isAppleMusicAvailable(): boolean {
  return !appleMusicUnavailable && Date.now() >= retryBlockedUntil;
}

function isUsable(token: AppleMusicToken | null): token is AppleMusicToken {
  return token !== null && token.expiresAt - Date.now() / 1000 > TOKEN_REUSE_MARGIN_SECONDS;
}

function readStoredToken(): AppleMusicToken | null {
  try {
    const raw = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { token, expiresAt } = parsed as Partial<AppleMusicToken>;
    if (typeof token !== 'string' || typeof expiresAt !== 'number') return null;
    return { token, expiresAt };
  } catch (error) {
    console.warn('[appleMusic] Could not read the stored Apple Music token', error);
    return null;
  }
}

function writeStoredToken(token: AppleMusicToken): void {
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(token));
  } catch (error) {
    console.warn('[appleMusic] Could not store the Apple Music token', error);
  }
}

function clearToken(): void {
  cachedToken = null;
  try {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.warn('[appleMusic] Could not clear the stored Apple Music token', error);
  }
}

function errorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') return undefined;
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

async function fetchToken(): Promise<string> {
  try {
    await ensureAnonymousAuth();
    const result = await getAppleMusicTokenCallable();
    const token = { token: result.data.token, expiresAt: result.data.expiresAt };
    cachedToken = token;
    writeStoredToken(token);
    return token.token;
  } catch (error) {
    if (errorCode(error) === 'functions/failed-precondition') {
      appleMusicUnavailable = true;
      console.warn('[appleMusic] Apple Music search is not configured; using iTunes');
    } else {
      retryBlockedUntil = Date.now() + RETRY_AFTER_ERROR_MS;
      console.warn('[appleMusic] Could not get an Apple Music token; retrying in 60 seconds', error);
    }
    throw error;
  }
}

async function getToken(): Promise<string> {
  if (appleMusicUnavailable) throw new Error('Apple Music search is not configured');
  if (isUsable(cachedToken)) return cachedToken.token;
  const stored = readStoredToken();
  if (isUsable(stored)) {
    cachedToken = stored;
    return stored.token;
  }
  if (Date.now() < retryBlockedUntil) throw new Error('Apple Music token is unavailable; retrying later');
  if (!tokenRequest) {
    tokenRequest = fetchToken().finally(() => {
      tokenRequest = null;
    });
  }
  return tokenRequest;
}

async function appleFetch(path: string): Promise<Response> {
  const request = (token: string) =>
    fetch(`${APPLE_MUSIC_CATALOG_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });

  const response = await request(await getToken());
  if (response.status !== 401 && response.status !== 403) return response;
  clearToken();
  return request(await getToken());
}

function artworkUrl(template: string | undefined, size: number): string {
  if (!template) return '';
  return template.replace('{w}', String(size)).replace('{h}', String(size));
}

function explicitnessOf(contentRating: string | undefined): SongSearchResult['trackExplicitness'] {
  if (contentRating === 'explicit') return 'explicit';
  if (contentRating === 'clean') return 'cleaned';
  return 'notExplicit';
}

function isrcOf(value: string | undefined): string | null {
  return typeof value === 'string' && ISRC_PATTERN.test(value) ? value.toUpperCase() : null;
}

export function appleSongToSearchResult(song: AppleSongResource): SongSearchResult | null {
  const attributes = song.attributes;
  if (!/^\d+$/.test(song.id) || !attributes) return null;
  return {
    trackId: Number(song.id),
    trackName: attributes.name ?? '',
    artistName: attributes.artistName ?? '',
    collectionName: attributes.albumName || 'Unknown Album',
    artworkUrl60: artworkUrl(attributes.artwork?.url, 60),
    artworkUrl100: artworkUrl(attributes.artwork?.url, 100),
    trackTimeMillis: attributes.durationInMillis || 0,
    trackExplicitness: explicitnessOf(attributes.contentRating),
    previewUrl: attributes.previews?.[0]?.url,
    isrc: isrcOf(attributes.isrc),
  };
}

function mapSongs(songs: AppleSongResource[] | undefined): SongSearchResult[] {
  return (songs ?? [])
    .map(appleSongToSearchResult)
    .filter((result): result is SongSearchResult => result !== null);
}

export async function searchAppleMusic(
  query: string,
  limit: number,
  mode: 'song' | 'artist',
): Promise<SongSearchResult[]> {
  const term = encodeURIComponent(query.trim()).replace(/%20/g, '+');
  const effectiveLimit = Math.min(mode === 'artist' ? MAX_APPLE_LIMIT : limit, MAX_APPLE_LIMIT);
  const response = await appleFetch(`/search?term=${term}&types=songs&limit=${effectiveLimit}`);
  if (!response.ok) throw new Error(`Apple Music search failed with status ${response.status}`);

  const body = (await response.json()) as AppleSearchResponse;
  const songs = mapSongs(body.results?.songs?.data);
  if (mode !== 'artist') return songs;

  const artist = query.toLowerCase().trim();
  const byArtist = songs.filter(song => song.artistName.toLowerCase().includes(artist));
  return byArtist.length < ARTIST_FILTER_MINIMUM ? songs : byArtist;
}

export async function lookupAppleMusicSong(trackId: number | string): Promise<SongSearchResult | null> {
  const response = await appleFetch(`/songs/${encodeURIComponent(String(trackId))}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Apple Music lookup failed with status ${response.status}`);
  const body = (await response.json()) as AppleSongsResponse;
  return mapSongs(body.data)[0] ?? null;
}
